import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Card, CardBody, CardHeader, Badge, Button, Progress, Table } from "reactstrap";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import { useGetAllTargetsQuery } from "store/features/target/targetApi";
import { useGetAllProjectsQuery, useGetAllClientsQuery } from "store/features/client/clientManagementApi";
import { useGetAllClientLeadsQuery } from "store/features/clientLeads/clientLeadsManagement";
import { useGetAllUsersQuery } from "store/features/users/userManagementApi";
import { useGetAllDevelopersQuery } from "store/features/developer/developerManagementApi";
import { toast } from "sonner";

const MarketingDashboard = () => {
  const user = useSelector(selectUser);
  const currentMonthStr = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [selectedUserId, setSelectedUserId] = useState("");

  const { data: targets = [], isLoading: targetsLoading } = useGetAllTargetsQuery();
  const { data: projects = [], isLoading: projectsLoading } = useGetAllProjectsQuery();
  const { data: clients = [] } = useGetAllClientsQuery();
  const { data: leads = [] } = useGetAllClientLeadsQuery();
  const { data: allUsers = [] } = useGetAllUsersQuery();
  const { data: developers = [] } = useGetAllDevelopersQuery();

  const isLoading = targetsLoading || projectsLoading;

  // Combine all team members/users for dropdown selector
  const allTeamUsers = useMemo(() => {
    const map = new Map();
    if (Array.isArray(allUsers)) {
      allUsers.forEach(u => {
        if (u && u.id) {
          map.set(String(u.id), {
            id: String(u.id),
            name: u.nickname || u.first_name || u.username || `User #${u.id}`,
            role: u.role || 'Marketing / Team'
          });
        }
      });
    }
    if (Array.isArray(developers)) {
      developers.forEach(u => {
        if (u && u.id && !map.has(String(u.id))) {
          map.set(String(u.id), {
            id: String(u.id),
            name: u.nickname || u.name || `User #${u.id}`,
            role: u.role || 'Developer'
          });
        }
      });
    }
    return Array.from(map.values());
  }, [allUsers, developers]);

  // Helper to check if a user is part of a project
  const isUserInProject = (project, targetUserId) => {
    if (!project || !targetUserId) return false;
    const uId = String(targetUserId);

    const checkMember = (val) => {
      if (!val) return false;
      if (Array.isArray(val)) {
        return val.some(item => {
          const itemId = typeof item === 'object' && item !== null ? item.id : item;
          return String(itemId) === uId;
        });
      }
      const itemId = typeof val === 'object' && val !== null ? val.id : val;
      return String(itemId) === uId;
    };

    return (
      checkMember(project.assigned_marketing_officer) ||
      checkMember(project.marketing_officer_id) ||
      checkMember(project.assigned_team) ||
      checkMember(project.assigned_project_manager) ||
      checkMember(project.assigned_frontend_dev) ||
      checkMember(project.assigned_backend_dev) ||
      checkMember(project.assigned_designer) ||
      checkMember(project.created_by)
    );
  };

  // Set default selectedUserId to logged in user or first user with a target
  useEffect(() => {
    if (!selectedUserId) {
      if (user?.id) {
        setSelectedUserId(String(user.id));
      } else if (targets.length > 0) {
        const monthTarget = targets.find(t => t.month === selectedMonth);
        if (monthTarget) {
          const tUser = typeof monthTarget.user === 'object' ? monthTarget.user?.id : monthTarget.user;
          if (tUser) setSelectedUserId(String(tUser));
        }
      }
    }
  }, [user?.id, selectedUserId, targets, selectedMonth]);

  const activeUserId = selectedUserId || (user?.id ? String(user.id) : "");

  // Filter Target for active user and selected month
  const currentTargetObj = useMemo(() => {
    if (activeUserId === "ALL") {
      // Sum target amounts of all users for selectedMonth
      const monthTargets = targets.filter(t => t.month === selectedMonth);
      const totalT = monthTargets.reduce((sum, t) => sum + (parseFloat(t.target_amount) || 0), 0);
      return { target_amount: totalT, commission_rate: 15 };
    }

    return targets.find(t => 
      t.month === selectedMonth && 
      (String(t.user) === String(activeUserId) || String(t.user?.id) === String(activeUserId))
    );
  }, [targets, selectedMonth, activeUserId]);

  const targetAmount = parseFloat(currentTargetObj?.target_amount || 0);
  const commissionRate = parseFloat(currentTargetObj?.commission_rate || 15); // Default 15% if not set

  // Calculate Projects assigned to this Marketing Officer in selectedMonth
  const monthlyAssignedProjects = useMemo(() => {
    if (!projects || !Array.isArray(projects)) return [];

    return projects.filter(p => {
      // Check assignment / creation date month (started in or before selectedMonth)
      const pDate = p.start_date || p.created_at || p.systemdate || p.date;
      const pMonth = (pDate && typeof pDate === "string") ? pDate.slice(0, 7) : selectedMonth;

      const isDateValid = !pMonth || pMonth <= selectedMonth;
      if (!isDateValid) return false;

      if (!activeUserId || activeUserId === "ALL") {
        return true;
      }

      return isUserInProject(p, activeUserId);
    });
  }, [projects, activeUserId, selectedMonth]);

  // Total Achieved Project Value in selectedMonth
  const totalAchievedSales = useMemo(() => {
    return monthlyAssignedProjects.reduce((sum, p) => sum + (parseFloat(p.budget) || 0), 0);
  }, [monthlyAssignedProjects]);

  // Overachievement calculations
  const overachievedAmount = Math.max(0, totalAchievedSales - targetAmount);
  const totalPotentialCommission = (overachievedAmount * commissionRate) / 100;
  const achievementPercentage = targetAmount > 0 ? Math.min(100, (totalAchievedSales / targetAmount) * 100) : 0;

  // Calculate Project-wise proportional commission
  const projectCommissionBreakdown = useMemo(() => {
    if (totalAchievedSales <= 0 || totalPotentialCommission <= 0) return [];

    return monthlyAssignedProjects.map(p => {
      const budget = parseFloat(p.budget) || 0;
      const projectCommissionShare = (budget / totalAchievedSales) * totalPotentialCommission;
      const isCompleted = p.status?.toLowerCase() === "completed" || p.status?.toLowerCase() === "complete";

      return {
        ...p,
        projectCommissionShare,
        commissionStatus: isCompleted ? "Released" : "Pending",
      };
    });
  }, [monthlyAssignedProjects, totalAchievedSales, totalPotentialCommission]);

  // Total Pending vs Released Commissions
  const { pendingCommission, releasedCommission } = useMemo(() => {
    let pending = 0;
    let released = 0;

    projectCommissionBreakdown.forEach(item => {
      if (item.commissionStatus === "Released") {
        released += item.projectCommissionShare;
      } else {
        pending += item.projectCommissionShare;
      }
    });

    return { pendingCommission: pending, releasedCommission: released };
  }, [projectCommissionBreakdown]);

  // Format currency
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-BD", {
      style: "currency",
      currency: "BDT",
      minimumFractionDigits: 2,
    }).format(Number(val) || 0);
  };

  const handleWithdrawRequest = () => {
    if (releasedCommission <= 0) {
      toast.error("No released commission available for withdrawal yet.");
      return;
    }
    toast.success(`Withdrawal request of ${formatCurrency(releasedCommission)} submitted! It will be added to your next salary payout.`);
  };

  if (isLoading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Marketing" breadcrumbItem="Marketing Dashboard" />

        {/* Month & User Filter Section */}
        <Card className="mb-4 shadow-sm border-0">
          <CardBody>
            <Row className="align-items-center">
              <Col md={5}>
                <h4 className="card-title text-primary mb-1">
                  <i className="fas fa-bullseye me-2"></i>Marketing Target & Commission Tracker
                </h4>
                <p className="text-muted mb-0 small">
                  Track monthly targets, assigned projects, and unlocked commissions.
                </p>
              </Col>
              <Col md={7} className="text-md-end mt-3 mt-md-0">
                <div className="d-flex flex-wrap justify-content-md-end gap-3 align-items-center">
                  {(user?.role === "super_admin" || user?.role === "admin") && (
                    <div className="text-start" style={{ minWidth: "220px" }}>
                      <label className="form-label fw-bold small text-muted mb-1">Select Team Member</label>
                      <select
                        className="form-select"
                        value={activeUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                      >
                        <option value="ALL">🌟 All Marketing / Company Projects</option>
                        {allTeamUsers.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.role})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="text-start" style={{ minWidth: "180px" }}>
                    <label className="form-label fw-bold small text-muted mb-1">Select Month</label>
                    <input
                      type="month"
                      className="form-control"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* KPI Cards Row */}
        <Row className="mb-4">
          <Col xl={3} md={6} className="mb-3 mb-xl-0">
            <Card className="shadow-sm border-0 border-start border-4 border-primary h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <span className="text-muted fw-semibold small">Monthly Target</span>
                    <h3 className="fw-bold text-primary mb-0 mt-1">{formatCurrency(targetAmount)}</h3>
                  </div>
                  <div className="avatar-sm bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center">
                    <i className="fas fa-flag fa-lg"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3} md={6} className="mb-3 mb-xl-0">
            <Card className="shadow-sm border-0 border-start border-4 border-info h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <span className="text-muted fw-semibold small">Achieved Sales (Projects)</span>
                    <h3 className="fw-bold text-info mb-0 mt-1">{formatCurrency(totalAchievedSales)}</h3>
                    <small className="text-muted">Target Met: {achievementPercentage.toFixed(1)}%</small>
                  </div>
                  <div className="avatar-sm bg-info bg-opacity-10 text-info rounded-circle d-flex align-items-center justify-content-center">
                    <i className="fas fa-chart-line fa-lg"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3} md={6} className="mb-3 mb-xl-0">
            <Card className="shadow-sm border-0 border-start border-4 border-warning h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <span className="text-muted fw-semibold small">Pending Commission</span>
                    <h3 className="fw-bold text-warning mb-0 mt-1">{formatCurrency(pendingCommission)}</h3>
                    <small className="text-muted">From ongoing projects</small>
                  </div>
                  <div className="avatar-sm bg-warning bg-opacity-10 text-warning rounded-circle d-flex align-items-center justify-content-center">
                    <i className="fas fa-clock fa-lg"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3} md={6} className="mb-3 mb-xl-0">
            <Card className="shadow-sm border-0 border-start border-4 border-success h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <span className="text-muted fw-semibold small">Released Commission</span>
                    <h3 className="fw-bold text-success mb-0 mt-1">{formatCurrency(releasedCommission)}</h3>
                    <small className="text-success fw-bold">Ready for Payroll / Payout</small>
                  </div>
                  <div className="avatar-sm bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center">
                    <i className="fas fa-wallet fa-lg"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Progress Bar & Overachievement Info */}
        <Card className="mb-4 shadow-sm border-0">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold text-dark">Target Progress ({selectedMonth})</span>
              <span className="fw-bold text-primary">{achievementPercentage.toFixed(1)}% Achieved</span>
            </div>
            <Progress value={achievementPercentage} color={achievementPercentage >= 100 ? "success" : "primary"} className="mb-3" style={{ height: "12px" }} />

            <div className="row text-center pt-2">
              <div className="col-md-4 border-end">
                <span className="text-muted small">Overachievement Sales</span>
                <h5 className="fw-bold text-dark mt-1">{formatCurrency(overachievedAmount)}</h5>
              </div>
              <div className="col-md-4 border-end">
                <span className="text-muted small">Commission Rate</span>
                <h5 className="fw-bold text-dark mt-1">{commissionRate}%</h5>
              </div>
              <div className="col-md-4">
                <span className="text-muted small">Total Commission Pool</span>
                <h5 className="fw-bold text-success mt-1">{formatCurrency(totalPotentialCommission)}</h5>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Projects & Commission Breakdown Table */}
        <Card className="shadow-sm border-0 mb-4">
          <CardHeader className="bg-white py-3 d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0 text-dark">
              <i className="fas fa-tasks me-2 text-primary"></i>Assigned Projects & Commission Breakdown
            </h5>
            <Button
              color="success"
              size="sm"
              disabled={releasedCommission <= 0}
              onClick={handleWithdrawRequest}
              className="px-3"
            >
              <i className="fas fa-hand-holding-usd me-2"></i>Withdraw Released ({formatCurrency(releasedCommission)})
            </Button>
          </CardHeader>
          <CardBody>
            {monthlyAssignedProjects.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="fas fa-folder-open fa-2x mb-2 d-block text-secondary"></i>
                No projects assigned to you for <strong>{selectedMonth}</strong> yet.
              </div>
            ) : (
              <Table responsive hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Project Name</th>
                    <th>Client</th>
                    <th>Assigned Date</th>
                    <th>Budget</th>
                    <th>Status</th>
                    <th>Commission Share</th>
                    <th>Commission Status</th>
                  </tr>
                </thead>
                <tbody>
                  {projectCommissionBreakdown.map((p) => (
                    <tr key={p.id}>
                      <td className="fw-bold text-primary">{p.name || p.title || `Project #${p.id}`}</td>
                      <td>{p.client_name || p.client?.company_name || "N/A"}</td>
                      <td>{p.start_date || p.created_at || selectedMonth}</td>
                      <td className="fw-semibold">{formatCurrency(p.budget)}</td>
                      <td>
                        <Badge color={p.status?.toLowerCase() === "completed" ? "success" : "warning"} className="px-2 py-1">
                          {p.status || "Ongoing"}
                        </Badge>
                      </td>
                      <td className="fw-bold text-dark">{formatCurrency(p.projectCommissionShare)}</td>
                      <td>
                        {p.commissionStatus === "Released" ? (
                          <Badge color="success" className="px-2 py-1">
                            <i className="fas fa-check-circle me-1"></i>Released / Unlocked
                          </Badge>
                        ) : (
                          <Badge color="warning" className="px-2 py-1">
                            <i className="fas fa-hourglass-half me-1"></i>Pending (Ongoing)
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default MarketingDashboard;
